/**
 * Reporting Tree
 *
 * The "Reporting" org view — a fast, indented reports-to hierarchy parented by
 * manager, rendered from the pre-built `GET /org/chart` tree (OrgChartResponse).
 * Replaces the hand-rolled spatial OrgChartVisx (node-graph + framer-motion-
 * per-node) that caused the 45s admin freeze.
 *
 * Performance: collapsed subtrees are never mounted — a node's children only
 * render when the node id is in `expanded`. No spatial layout, no per-node
 * animation. Initial state: root nodes visible, everything else collapsed.
 *
 * Search jumps to the match (expands the whole ancestor path + scrolls + rings)
 * instead of leaving an "ancestor wall" — the expanded path IS the breadcrumb.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronRight, ChevronDown, Search, X, Loader2, Users,
  ChevronsDownUp, ChevronsUpDown, Briefcase, MapPin, Coins,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { OrgChartNode, OrgChartResponse } from '@/services/api/organizationService';
import { useUserPhoto } from '@/hooks/useUserPhoto';
import { orgRowPadding, orgRowTint } from './orgTreeDepth';

// =============================================================================
// CONSTANTS / HELPERS
// =============================================================================

const FILLED_COLOR = '#1D9E75'; // teal — filled seats (mirrors OrgStructureTree)

function getInitials(name?: string | null): string {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

// Flat search index entry (built once from the loaded tree).
interface IndexEntry {
  id: string;
  userName?: string;
  positionTitle?: string;
  positionCode?: string;
  department?: string;
  campus?: string;
  userId?: number;
  positionId?: number;
  isVacant: boolean;
  label: string;       // display name used for sorting / breadcrumb
  path: string[];      // ancestor ids from root down to and incl. self
}

// =============================================================================
// TREE BRANCH (recursive; collapsed subtrees never mount)
// =============================================================================

interface BranchProps {
  node: OrgChartNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  registerRef: (id: string, el: HTMLElement | null) => void;
  highlightId: string | null;
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  // When filters are active, ids of nodes that themselves match. Nodes NOT in the
  // set are retained only to keep a matching descendant's reporting chain intact,
  // so they render dimmed. null = no filters active (nothing dimmed).
  matchIds: Set<string> | null;
  // While filters are active the (already-pruned) tree is shown fully expanded so
  // deep matches aren't hidden under a collapsed ancestor. This is DERIVED — it
  // never mutates `expanded` — so clearing the filters reverts to the user's own
  // expand state with nothing to unwind.
  forceExpand?: boolean;
  // While `forceExpand`, nodes are expanded-by-default EXCEPT those the user
  // explicitly collapsed this filter session (so the chevrons still work).
  collapsedInFilter?: Set<string>;
}

const TreeBranch: React.FC<BranchProps> = (props) => {
  const { node, depth, expanded, onToggle, registerRef, highlightId, onOpenPerson, matchIds, forceExpand, collapsedInFilter } = props;
  const isExpanded = forceExpand
    ? !collapsedInFilter?.has(node.id)
    : expanded.has(node.id);
  const hasChildren = node.children.length > 0;
  const highlighted = highlightId === node.id;
  const dimmed = matchIds != null && !matchIds.has(node.id);
  const vacant = node.is_vacant || !node.user_id;
  const photo = useUserPhoto(vacant ? null : node.user_id);
  const reports = Math.round(node.direct_reports_count);
  const meta = [node.department, node.campus].filter(Boolean).join(' · ');

  const openPerson = () => {
    if (node.position_id != null) onOpenPerson?.(node.user_id ?? null, node.position_id);
    else if (hasChildren) onToggle(node.id);
  };

  return (
    <div>
      <div
        ref={(el) => registerRef(node.id, el)}
        style={{
          paddingLeft: orgRowPadding(depth),
          ...(highlighted
            ? { boxShadow: `inset 0 0 0 1px ${FILLED_COLOR}`, backgroundColor: 'rgba(29,158,117,0.10)' }
            : {}),
        }}
        className={cn(
          'flex items-center gap-2 border-b border-border/40 py-2 pr-3 transition-colors',
          !highlighted && orgRowTint(depth),
          !highlighted && 'hover:bg-muted/60',
          dimmed && !highlighted && 'opacity-45',
        )}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
            className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-muted"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-5 shrink-0" aria-hidden />
        )}

        <button
          type="button"
          onClick={openPerson}
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md py-0.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-heritage-blue/50"
        >
          {vacant ? (
            <span className="flex items-center gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
              <span className="font-medium">Vacant</span>
              {node.position_code && (
                <span className="font-mono text-amber-600/80 dark:text-amber-300/70">{node.position_code}</span>
              )}
              {node.acting_for_reports && (
                <span className="truncate" title="Absentee approver: acts as people leader for this seat's reports while it is vacant">
                  · acting: <span className="font-medium">{node.acting_for_reports}</span>
                </span>
              )}
            </span>
          ) : (
            <Avatar className="h-6 w-6 shrink-0">
              {photo && <AvatarImage src={photo} alt={node.user_name || ''} />}
              <AvatarFallback className="bg-heritage-blue/10 text-[10px] text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
                {getInitials(node.user_name)}
              </AvatarFallback>
            </Avatar>
          )}
          {!vacant && (
            <span className="truncate font-medium text-foreground">{node.user_name || 'Vacant'}</span>
          )}
          {node.position_title && (
            <span className="truncate text-sm text-muted-foreground">{node.position_title}</span>
          )}
          {!vacant && node.line_manager_vacant && node.absentee_approver_name && (
            <span
              className="hidden shrink-0 items-center rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-500/10 dark:text-amber-300 sm:inline-flex"
              title={`Line manager seat is vacant — ${node.absentee_approver_name} acts as people leader${node.absentee_override ? ' (recorded decision)' : ''}`}
            >
              acting PL: {node.absentee_approver_name}
            </span>
          )}
          {node.is_critical && (
            <Badge variant="outline" className="hidden shrink-0 text-[10px] text-heritage-blue dark:text-heritage-blue-100 sm:inline-flex">
              Critical
            </Badge>
          )}
        </button>

        <div className="ml-auto flex shrink-0 items-center gap-3">
          {meta && <span className="hidden max-w-[16rem] truncate text-[11px] text-muted-foreground md:inline">{meta}</span>}
          {reports > 0 && (
            <span className="whitespace-nowrap text-[11px] text-muted-foreground">
              {reports.toLocaleString()} report{reports !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {isExpanded && hasChildren &&
        node.children.map((child) => (
          <TreeBranch key={child.id} {...props} node={child} depth={depth + 1} />
        ))}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export interface ReportingTreeProps {
  data: OrgChartResponse | null;
  onOpenPerson?: (userId: number | null, positionId: number) => void;
  // Self-populated facet option lists (campus code + F&O financial dimensions),
  // lifted from OrganizationPage's positions so both trees share one source of
  // truth. Active-filter state stays internal to this component.
  campusOptions?: string[];
  dimCampusOptions?: string[];
  dimDeptOptions?: string[];
  dimFacultyOptions?: string[];
  workerTypeOptions?: string[];   // incumbent employment_category (filled seats)
  positionTypeOptions?: string[]; // seat's F&O type normalized (incl. vacant)
}

export const ReportingTree: React.FC<ReportingTreeProps> = ({
  data,
  onOpenPerson,
  campusOptions = [],
  dimCampusOptions = [],
  dimDeptOptions = [],
  dimFacultyOptions = [],
  workerTypeOptions = [],
  positionTypeOptions = [],
}) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // While filtering the pruned tree renders expanded-by-default (so deep matches
  // aren't buried); this holds the nodes the user has EXPLICITLY collapsed during
  // the current filter session. Kept separate from `expanded` so it never leaks
  // into the unfiltered view — clearing the filter reverts to `expanded` untouched.
  const [collapsedInFilter, setCollapsedInFilter] = useState<Set<string>>(new Set());

  // Search
  const [term, setTerm] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Filters — campus + F&O financial dimensions. A node is VISIBLE when it (or
  // any descendant) matches ALL active filters, so ancestor reporting lines above
  // a match are retained (dimmed).
  const [campusFilter, setCampusFilter] = useState<string>('all');
  const [dimCampusFilter, setDimCampusFilter] = useState<string>('all');
  const [dimDeptFilter, setDimDeptFilter] = useState<string>('all');
  const [dimFacultyFilter, setDimFacultyFilter] = useState<string>('all');
  // Two employment axes. Worker type = incumbent's employment_category (a vacant
  // node has no worker → can't match a worker-type filter). Position type = the
  // seat's own F&O type normalized (present incl. when vacant).
  const [workerTypeFilter, setWorkerTypeFilter] = useState<Set<string>>(new Set());
  const [positionTypeFilter, setPositionTypeFilter] = useState<Set<string>>(new Set());

  // Jump / highlight
  const [focusedPath, setFocusedPath] = useState<string[] | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => () => { if (highlightTimer.current) clearTimeout(highlightTimer.current); }, []);

  // Close the search dropdown on an outside click.
  useEffect(() => {
    if (!showResults) return;
    const onDown = (e: PointerEvent) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [showResults]);

  // --- Search index + path map (built once per data) -------------------------
  const { searchIndex, pathById } = useMemo(() => {
    const searchIndex: IndexEntry[] = [];
    const pathById = new Map<string, string[]>();
    if (!data?.root_nodes) return { searchIndex, pathById };

    const walk = (node: OrgChartNode, ancestors: string[]) => {
      const path = [...ancestors, node.id];
      pathById.set(node.id, path);
      searchIndex.push({
        id: node.id,
        userName: node.user_name,
        positionTitle: node.position_title,
        positionCode: node.position_code,
        department: node.department,
        campus: node.campus,
        userId: node.user_id,
        positionId: node.position_id,
        isVacant: node.is_vacant || !node.user_id,
        label: node.user_name || node.position_title || node.position_code || 'Vacant',
        path,
      });
      node.children.forEach((c) => walk(c, path));
    };
    data.root_nodes.forEach((r) => walk(r, []));
    return { searchIndex, pathById };
  }, [data]);

  // --- Search results (client-side over the loaded tree) ---------------------
  const results = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return [];
    return searchIndex
      .filter((e) =>
        (e.userName?.toLowerCase().includes(q) ?? false) ||
        (e.positionTitle?.toLowerCase().includes(q) ?? false) ||
        (e.positionCode?.toLowerCase().includes(q) ?? false) ||
        (e.department?.toLowerCase().includes(q) ?? false),
      )
      .sort((a, b) => {
        const as = a.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bs = b.label.toLowerCase().startsWith(q) ? 0 : 1;
        return as - bs;
      })
      .slice(0, 20);
  }, [term, searchIndex]);

  // --- Campus / financial-dimension filters (keep-ancestor-path) --------------
  const filtersActive =
    campusFilter !== 'all' || dimCampusFilter !== 'all' || dimDeptFilter !== 'all' ||
    dimFacultyFilter !== 'all' || workerTypeFilter.size > 0 || positionTypeFilter.size > 0;

  const nodeSelfMatches = useCallback(
    (n: OrgChartNode): boolean => {
      if (campusFilter !== 'all' && n.campus_code !== campusFilter) return false;
      if (dimCampusFilter !== 'all' && n.dimension_campus !== dimCampusFilter) return false;
      if (dimDeptFilter !== 'all' && n.dimension_department !== dimDeptFilter) return false;
      if (dimFacultyFilter !== 'all' && n.dimension_faculty !== dimFacultyFilter) return false;
      // Worker type reads the incumbent's employment_category (vacant node has none →
      // can't match); position type reads the node's own normalized F&O type.
      if (workerTypeFilter.size > 0 && !workerTypeFilter.has(n.employment_category ?? '')) return false;
      if (positionTypeFilter.size > 0 && !positionTypeFilter.has(n.position_type_group ?? '')) return false;
      return true;
    },
    [campusFilter, dimCampusFilter, dimDeptFilter, dimFacultyFilter, workerTypeFilter, positionTypeFilter],
  );

  // Prune to nodes that match, plus every ancestor on the path to a match. A kept
  // ancestor that doesn't itself match is retained (dimmed via matchIds) so the
  // reporting chain above a match stays intact; subtrees with no match are dropped.
  const { displayRoots, matchIds } = useMemo(() => {
    const roots = data?.root_nodes || [];
    if (!filtersActive) return { displayRoots: roots, matchIds: null as Set<string> | null };
    const ids = new Set<string>();
    const prune = (node: OrgChartNode): OrgChartNode | null => {
      const keptChildren: OrgChartNode[] = [];
      for (const child of node.children) {
        const pc = prune(child);
        if (pc) keptChildren.push(pc);
      }
      const selfMatch = nodeSelfMatches(node);
      if (selfMatch) ids.add(node.id);
      if (selfMatch || keptChildren.length > 0) {
        return { ...node, children: keptChildren };
      }
      return null;
    };
    const out: OrgChartNode[] = [];
    for (const r of roots) {
      const p = prune(r);
      if (p) out.push(p);
    }
    return { displayRoots: out, matchIds: ids };
  }, [data, filtersActive, nodeSelfMatches]);

  // NB: while filtering we reveal the whole pruned tree via the DERIVED
  // `forceExpand` prop (threaded to TreeBranch below), never by writing to
  // `expanded`. That way clearing the filters reverts to the user's own expand
  // state with nothing to unwind — no lingering force-expansion over the full org.

  const clearFilters = useCallback(() => {
    setCampusFilter('all');
    setDimCampusFilter('all');
    setDimDeptFilter('all');
    setDimFacultyFilter('all');
    setWorkerTypeFilter(new Set());
    setPositionTypeFilter(new Set());
    setCollapsedInFilter(new Set()); // fresh filter session next time
  }, []);

  const toggleWorkerType = useCallback((e: string) => {
    setWorkerTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }, []);

  const togglePositionType = useCallback((e: string) => {
    setPositionTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(e)) next.delete(e);
      else next.add(e);
      return next;
    });
  }, []);

  // --- Expand / collapse -----------------------------------------------------
  // While filtering, the chevrons toggle the filter-scoped `collapsedInFilter`
  // set (expanded-by-default); otherwise they toggle the real `expanded` set.
  const toggle = useCallback((id: string) => {
    const flip = (prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    };
    if (filtersActive) setCollapsedInFilter(flip);
    else setExpanded(flip);
  }, [filtersActive]);

  const collapseAll = useCallback(() => {
    // While filtering: collapse to the visible roots (mark them collapsed so
    // their subtrees hide). Otherwise: clear the real expand state.
    if (filtersActive) setCollapsedInFilter(new Set(displayRoots.map((r) => r.id)));
    else setExpanded(new Set());
    setFocusedPath(null);
    setHighlightId(null);
  }, [filtersActive, displayRoots]);

  const expandFirstLevel = useCallback(() => {
    // While filtering: reveal everything (the filter default) by clearing the
    // user's collapses. Otherwise: expand only the top level.
    if (filtersActive) {
      setCollapsedInFilter(new Set());
      return;
    }
    if (!data?.root_nodes) return;
    setExpanded(new Set(data.root_nodes.map((r) => r.id)));
  }, [filtersActive, data]);

  const registerRef = useCallback((id: string, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);

  // --- Jump to a node --------------------------------------------------------
  const jumpTo = useCallback(
    (nodeId: string) => {
      const path = pathById.get(nodeId);
      if (!path) return;

      // A search jump takes priority over active filters — clear them so the
      // target is never pruned out of the visible tree.
      clearFilters();

      setExpanded((prev) => {
        const next = new Set(prev);
        path.forEach((id) => next.add(id));
        return next;
      });
      setFocusedPath(path);
      setHighlightId(nodeId);
      setShowResults(false);

      requestAnimationFrame(() => {
        setTimeout(() => {
          rowRefs.current.get(nodeId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 60);
      });

      if (highlightTimer.current) clearTimeout(highlightTimer.current);
      highlightTimer.current = setTimeout(() => setHighlightId(null), 2600);
    },
    [pathById, clearFilters],
  );

  const clearSearch = useCallback(() => {
    setTerm('');
    setShowResults(false);
  }, []);

  // Breadcrumb names for the focused node.
  const breadcrumb = useMemo(() => {
    if (!focusedPath) return [];
    return focusedPath.map((id) => {
      const e = searchIndex.find((x) => x.id === id);
      return e ? e.label : id;
    });
  }, [focusedPath, searchIndex]);

  // =============================================================================
  // RENDER
  // =============================================================================

  if (!data) {
    return (
      <div className="flex h-full flex-col items-center justify-center rounded-lg border border-border bg-card">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">Loading reporting structure…</p>
      </div>
    );
  }

  const roots = data.root_nodes || [];

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-card">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/40 p-3">
        {/* Search */}
        <div ref={searchBoxRef} className="relative min-w-[16rem] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={term}
            onChange={(e) => { setTerm(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder="Search people, positions or teams…"
            className="h-9 pl-9 pr-8"
          />
          {term && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-muted"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}

          {showResults && term.trim() && (
            <div className="absolute left-0 top-full z-50 mt-1 max-h-[24rem] w-full min-w-[22rem] overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
              {results.length === 0 ? (
                <div className="px-3 py-4 text-center text-sm text-muted-foreground">No matches</div>
              ) : (
                results.map((r) => (
                  <ResultRow key={r.id} entry={r} onClick={() => jumpTo(r.id)} />
                ))
              )}
            </div>
          )}
        </div>

        {/* Campus + F&O financial-dimension filters (Coins). A node stays visible
            when it OR a descendant matches, so the reporting chain above a match
            is retained (dimmed). */}
        <Select value={campusFilter} onValueChange={setCampusFilter}>
          <SelectTrigger className="h-9 w-[140px]"><MapPin className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campuses</SelectItem>
            {campusOptions.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimCampusFilter} onValueChange={setDimCampusFilter}>
          <SelectTrigger className="h-9 w-[150px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Fin. campus" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. campuses</SelectItem>
            {dimCampusOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimDeptFilter} onValueChange={setDimDeptFilter}>
          <SelectTrigger className="h-9 w-[170px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Fin. department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All fin. departments</SelectItem>
            {dimDeptOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={dimFacultyFilter} onValueChange={setDimFacultyFilter}>
          <SelectTrigger className="h-9 w-[160px]"><Coins className="mr-2 h-4 w-4 text-muted-foreground" /><SelectValue placeholder="Faculty" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All faculties</SelectItem>
            {dimFacultyOptions.map((d) => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Worker type = incumbent's employment_category (filled nodes only). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', workerTypeFilter.size > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Worker type
              {workerTypeFilter.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{workerTypeFilter.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by worker type (filled seats)</DropdownMenuLabel>
            {workerTypeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No worker types</div>
            )}
            {workerTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem
                key={e}
                checked={workerTypeFilter.has(e)}
                onCheckedChange={() => toggleWorkerType(e)}
                onSelect={(ev) => ev.preventDefault()}
              >
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {workerTypeFilter.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setWorkerTypeFilter(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear worker type
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* Position type = node's F&O type normalized (present incl. when vacant). */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className={cn('h-9', positionTypeFilter.size > 0 && 'border-heritage-blue')}>
              <Briefcase className="mr-1.5 h-3.5 w-3.5" /> Position type
              {positionTypeFilter.size > 0 && (
                <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{positionTypeFilter.size}</Badge>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Filter by position type (incl. vacant)</DropdownMenuLabel>
            {positionTypeOptions.length === 0 && (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">No position types</div>
            )}
            {positionTypeOptions.map((e) => (
              <DropdownMenuCheckboxItem
                key={e}
                checked={positionTypeFilter.has(e)}
                onCheckedChange={() => togglePositionType(e)}
                onSelect={(ev) => ev.preventDefault()}
              >
                <span className="text-xs font-medium">{e}</span>
              </DropdownMenuCheckboxItem>
            ))}
            {positionTypeFilter.size > 0 && (
              <>
                <DropdownMenuSeparator />
                <button
                  type="button"
                  onClick={() => setPositionTypeFilter(new Set())}
                  className="w-full px-2 py-1.5 text-left text-xs text-destructive hover:bg-muted"
                >
                  Clear position type
                </button>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {filtersActive && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={clearFilters}>
            <X className="mr-1 h-3.5 w-3.5" /> Clear
          </Button>
        )}

        <div className="ml-auto flex items-center gap-1.5">
          <Button variant="outline" size="sm" className="h-9" onClick={expandFirstLevel}>
            <ChevronsUpDown className="mr-1 h-3.5 w-3.5" /> Expand top level
          </Button>
          <Button variant="outline" size="sm" className="h-9" onClick={collapseAll}>
            <ChevronsDownUp className="mr-1 h-3.5 w-3.5" /> Collapse all
          </Button>
        </div>
      </div>

      {/* Breadcrumb for the focused node */}
      {breadcrumb.length > 0 && (
        <div className="flex items-center gap-1.5 border-b border-border bg-muted/20 px-3 py-1.5 text-xs text-muted-foreground">
          <span className="font-medium text-foreground/70">Focused:</span>
          {breadcrumb.map((name, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="h-3 w-3" />}
              <span className={cn('truncate', i === breadcrumb.length - 1 && 'font-medium text-foreground')}>{name}</span>
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={() => { setFocusedPath(null); setHighlightId(null); }}
            className="ml-2 rounded p-0.5 hover:bg-muted"
            aria-label="Clear focus"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}

      {/* Tree */}
      <div className="min-h-0 flex-1 overflow-auto">
        {roots.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-16 text-center">
            <Users className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium text-foreground">No reporting hierarchy to show</p>
            <p className="text-xs text-muted-foreground">Sync from F&amp;O to populate the reporting lines.</p>
          </div>
        ) : displayRoots.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center py-16 text-center">
            <Users className="mb-3 h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium text-foreground">No one matches the current filters</p>
            <p className="text-xs text-muted-foreground">Try clearing the campus / dimension filters.</p>
          </div>
        ) : (
          displayRoots.map((r) => (
            <TreeBranch
              key={r.id}
              node={r}
              depth={0}
              expanded={expanded}
              onToggle={toggle}
              registerRef={registerRef}
              highlightId={highlightId}
              onOpenPerson={onOpenPerson}
              matchIds={matchIds}
              forceExpand={filtersActive}
              collapsedInFilter={collapsedInFilter}
            />
          ))
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
        <span>
          {Math.round(data.total_employees).toLocaleString()} people ·{' '}
          {Math.round(data.total_positions).toLocaleString()} positions
          {data.vacant_positions > 0 && (
            <span className="text-amber-600 dark:text-amber-400"> · {Math.round(data.vacant_positions).toLocaleString()} vacant</span>
          )}
          {' · '}{Math.round(data.levels).toLocaleString()} levels
        </span>
        <span>Generated {new Date(data.generated_at).toLocaleString()}</span>
      </div>
    </div>
  );
};

// Search-result row (own component for the photo hook).
const ResultRow: React.FC<{ entry: IndexEntry; onClick: () => void }> = ({ entry, onClick }) => {
  const photo = useUserPhoto(entry.isVacant ? null : entry.userId);
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-muted"
    >
      {entry.isVacant ? (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-dashed border-amber-400 bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">
          <Briefcase className="h-3 w-3" />
        </span>
      ) : (
        <Avatar className="h-6 w-6 shrink-0">
          {photo && <AvatarImage src={photo} alt={entry.userName || ''} />}
          <AvatarFallback className="bg-heritage-blue/10 text-[10px] text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
            {getInitials(entry.userName)}
          </AvatarFallback>
        </Avatar>
      )}
      <span className="truncate font-medium text-foreground">{entry.userName || 'Vacant'}</span>
      {entry.positionTitle && (
        <span className="truncate text-xs text-muted-foreground">{entry.positionTitle}</span>
      )}
      {entry.department && (
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{entry.department}</span>
      )}
    </button>
  );
};

export default ReportingTree;
